import React, { useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { Close, FileUpload } from '@mui/icons-material';

interface FileUploadAreaProps {
  file: File | null;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearFile: () => void;
  accept?: string;
  disabled?: boolean;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  dragTitle?: string;
  dragSubtitle?: string;
  multiple?: boolean;
  onError?: (message: string) => void;
}

const FileUploadArea: React.FC<FileUploadAreaProps> = ({
  file,
  onFileChange,
  onClearFile,
  accept = '.csv',
  disabled = false,
  title = 'Carregar ficheiro',
  subtitle = 'Arraste o ficheiro ou clique para selecionar',
  buttonText = 'Escolher ficheiro',
  dragTitle = 'Solte o ficheiro aqui',
  dragSubtitle = 'Solte para carregar',
  multiple = false,
  onError,
}) => {
  const theme = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);

    if (!multiple && files.length > 1) {
      onError?.('Por favor, selecione apenas um ficheiro');
      return;
    }

    const targetFile = files[0];

    // Validate file type if accept is specified
    if (accept && !isFileTypeAccepted(targetFile, accept)) {
      const acceptedTypes = accept
        .split(',')
        .map((type) => type.trim())
        .join(', ');
      onError?.(`Por favor, selecione ficheiros do tipo: ${acceptedTypes}`);
      return;
    }

    // Create synthetic event to match the expected interface
    const syntheticEvent = {
      target: { files: multiple ? files : [targetFile] },
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    onFileChange(syntheticEvent);
  };

  const handleBoxClick = () => {
    if (disabled) return;
    const fileInput = document.querySelector('input[type="file"][data-file-upload-area]') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  const isFileTypeAccepted = (file: File, acceptString: string): boolean => {
    const acceptedTypes = acceptString.split(',').map((type) => type.trim());

    return acceptedTypes.some((type) => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      return file.type === type;
    });
  };

  return (
    <>
      <Box
        onClick={handleBoxClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: isDragOver ? `1px dashed ${theme.palette.primary.main}` : `1px dashed ${theme.palette.grey[300]}`,
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          backgroundColor: isDragOver ? theme.palette.action.hover : theme.palette.background.paper,
          transition: 'all 0.2s ease',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          '&:hover': !disabled
            ? {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover,
              }
            : {},
        }}
      >
        <FileUpload
          sx={{
            fontSize: 48,
            color: isDragOver ? theme.palette.primary.main : theme.palette.grey[400],
            mb: 1,
          }}
        />
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
          {isDragOver ? dragTitle : title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isDragOver ? dragSubtitle : subtitle}
        </Typography>
        <Button variant="contained" disabled={disabled} sx={{ textTransform: 'none', pointerEvents: 'none' }}>
          {buttonText}
        </Button>
        <input type="file" hidden accept={accept} onChange={onFileChange} disabled={disabled} multiple={multiple} data-file-upload-area />
      </Box>

      {file && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FileUpload sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {file.name}
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={onClearFile}
              disabled={disabled}
              sx={{
                minWidth: 'auto',
                p: 0.5,
                color: theme.palette.text.primary,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.error.main,
                },
              }}
            >
              <Close fontSize="small" />
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default FileUploadArea;
